import type { Observable } from "rxjs";
import { zip as observableZip } from "rxjs";

import type { IScope, ITimeoutService, IWindowService } from "angular";
import { cloneDeep, forEach, isEqual, map, remove } from "lodash";
import { finalize, tap } from "rxjs/operators";

import type { IProfileService } from "App/Modules/Auth";
import type { ICoverageCalculationsService } from "App/Modules/Gui";
import type {
    IFunctionality,
    IMap,
    IPoint,
    IResponse,
    IResponseWithLoading,
    ISite,
    ISiteEquipmentWithCoverages,
    IVideoStream,
    IViewCoverage,
} from "App/Modules/Models";
import { EquipmentClass, Permission, SiteState, StreamSplitType } from "App/Modules/Models";
import type {
    ISiteEquipmentRepository,
    ISiteFunctionalitiesRepository,
    ISitesRepository,
} from "App/Modules/Repositories";
import type { IModalService, IPageService, IScreenService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";
import type ISiteEquipmentListService from "../../../../Services/EquipmentList/ISiteEquipmentListService";

export default class ProjectionMapComponentController {
    public static $inject: string[] = [
        "$scope",
        "$window",
        "$timeout",
        "smoothScroll",
        "debounce",
        "mmSiteEquipmentRepository",
        "mmSitesRepository",
        "mmSiteFunctionalitiesRepository",
        "mmCoverageCalculationsService",
        "mmModalService",
        "irPageService",
        "mmProfileService",
        "irScreenService",
        "irSiteEquipmentListService",
    ];

    public site: ISite;

    public siteEquipment: IResponse<ISiteEquipmentWithCoverages[]>;

    public functionalities: IResponse<IFunctionality[]>;

    public selectedSiteEquipment: ISiteEquipmentWithCoverages;

    public map: IResponse<IMap>;

    public editMap = false;

    public isEditable = false;

    public isCollapsed = false;

    public isLoading = true;

    public addToMapResponse: IResponseWithLoading<ISiteEquipmentWithCoverages>;

    public patchSiteEquipmentResponse: IResponseWithLoading<ISiteEquipmentWithCoverages>;

    public patchViewCoverageResponse: IResponseWithLoading<IViewCoverage>;

    private selectedSiteEquipmentWatch: () => void;

    private debouncers: any = {};

    private patchDeltas: { [id: string]: IViewCoverage } = {};

    private viewCoveragesLookup: { [id: string]: IViewCoverage };

    private videoStreamsLookup: { [id: string]: IVideoStream };

    private mapChanged = false;

    constructor(
        private readonly $scope: IScope,
        private readonly $window: IWindowService,
        private readonly $timeout: ITimeoutService,
        private readonly smoothScroll: any,
        private readonly debounce: any,
        private readonly siteEquipmentRepository: ISiteEquipmentRepository,
        private readonly sitesRepository: ISitesRepository,
        private readonly siteFunctionalitiesRepository: ISiteFunctionalitiesRepository,
        private readonly coverageCalculationsService: ICoverageCalculationsService,
        private readonly modalService: IModalService,
        private readonly pageService: IPageService,
        private readonly profileService: IProfileService,
        private readonly screenService: IScreenService,
        private readonly siteEquipmentListService: ISiteEquipmentListService,
    ) {}

    public $onInit() {
        this.updateScreenSizeRestrictions();

        this.$scope.$watch("$ctrl.editMap", (value) => {
            if (!value) {
                this.setupComponent();
                this.unselectEquipment();
            }
        });

        this.$window.addEventListener("orientationchange", this.changeOrientation, false);
    }

    public $onDestroy() {
        this.$window.removeEventListener("orientationchange", this.changeOrientation, false);
    }

    public changeOrientation = () => {
        this.updateScreenSizeRestrictions();
    };

    public equipmentDragStart(siteEquipment: ISiteEquipmentWithCoverages) {
        this.selectEquipment(siteEquipment);
    }

    public equipmentDragStop(siteEquipment: ISiteEquipmentWithCoverages) {
        const normalized = cloneDeep(siteEquipment.positionOnMap);
        this.coverageCalculationsService.toNormalized(normalized, this.map.data.image);
        this.siteEquipmentRepository
            .patch(this.site.id, siteEquipment.id, {
                positionOnMap: normalized,
            } as ISiteEquipmentWithCoverages)
            .subscribe();
    }

    public selectEquipment(siteEquipment: ISiteEquipmentWithCoverages) {
        if (this.selectedSiteEquipment && this.selectedSiteEquipment !== siteEquipment) {
            this.selectedSiteEquipment.isSelected = false;
        }

        this.selectedSiteEquipment = siteEquipment;
        this.selectedSiteEquipment.isSelected = true;
        this.$scope.$broadcast("slider.resize");

        if (this.selectedSiteEquipmentWatch) {
            this.selectedSiteEquipmentWatch();
            this.selectedSiteEquipmentWatch = null;
        }

        if (!this.selectedSiteEquipmentWatch) {
            this.selectedSiteEquipmentWatch = this.$scope.$watch(
                () => this.selectedSiteEquipmentWatcherExpression(),
                (newValue: ISiteEquipmentWithCoverages, oldValue: ISiteEquipmentWithCoverages) =>
                    this.selectedSiteEquipmentWatcher(newValue, oldValue),
                true,
            );
        }
    }

    public unselectEquipment() {
        if (this.selectedSiteEquipment) {
            this.selectedSiteEquipment.isSelected = false;
            this.selectedSiteEquipment = null;

            if (this.selectedSiteEquipmentWatch) {
                this.selectedSiteEquipmentWatch();
                this.selectedSiteEquipmentWatch = null;
            }
        }
    }

    public toggleSelectedEquipment(siteEquipment: ISiteEquipmentWithCoverages) {
        if (siteEquipment.isSelected) {
            this.unselectEquipment();
        } else {
            this.selectEquipment(siteEquipment);
        }
    }

    public addSiteEquipment() {
        this.modalService.open<ISiteEquipmentWithCoverages, string>("mmAddSiteEquipment", null, this.site.id).subscribe(
            (addedSiteEquipmentResponse) => {
                addedSiteEquipmentResponse.isConnectedToEquipment = false;
                addedSiteEquipmentResponse.rotationOnMap = 0;
                addedSiteEquipmentResponse.videoStreams = [];
                addedSiteEquipmentResponse.viewCoverages = [];
                addedSiteEquipmentResponse.functionalities = [];

                this.siteEquipment.data.push(addedSiteEquipmentResponse);
                this.addSiteEquipmentToMap(addedSiteEquipmentResponse);
            },
            (reason) => reason,
        );
    }

    public addSiteEquipmentToMap(siteEquipment: ISiteEquipmentWithCoverages) {
        const positionOnMap = { x: 0.5, y: 0.5 };

        const equipmentElevation = 2;

        const patchObject = {
            equipmentElevation,
            positionOnMap,
            rotationOnMap: 0,
        } as ISiteEquipmentWithCoverages;

        this.addToMapResponse = { isLoading: true };

        extendWithLoadingAndFail(this.siteEquipmentRepository.patch(this.site.id, siteEquipment.id, patchObject))
            .pipe(
                finalize(() => {
                    this.$scope.$applyAsync();
                    if (
                        siteEquipment.class === EquipmentClass.Camera ||
                        siteEquipment.class === EquipmentClass.AnalogCamera
                    ) {
                        this.addViewCoverage(siteEquipment);
                    }
                }),
            )
            .subscribe(
                (response: IResponseWithLoading<ISiteEquipmentWithCoverages>) => {
                    if (!response.isLoading && response.data) {
                        this.addToMapResponse = response;
                        this.coverageCalculationsService.toAbsolute(response.data.positionOnMap, this.map.data.image);

                        siteEquipment.positionOnMap = response.data.positionOnMap;
                        siteEquipment.equipmentElevation = response.data.equipmentElevation;
                        siteEquipment.rotationOnMap = response.data.rotationOnMap;

                        this.selectEquipment(siteEquipment);
                        this.$scope.$broadcast("slider.resize");
                    }
                },
                (error) => {
                    this.addToMapResponse = error;
                },
            );
    }

    public deleteSiteEquipment(siteEquipment: ISiteEquipmentWithCoverages) {
        remove(this.siteEquipment.data, siteEquipment);
        this.unselectEquipment();
    }

    public get siteState(): SiteState {
        return this.site ? null : SiteState[this.site.siteState as any as keyof typeof SiteState];
    }

    public hasEditPermission(): boolean {
        return this.profileService.hasPermission(Permission.SiteBilling);
    }

    public isEditModeAvailable(): boolean {
        return this.hasEditPermission() && this.site.siteState !== SiteState.Normal;
    }

    public toggleEditMode() {
        this.isEditable = !this.isEditable;
    }

    public toggleMapChanged() {
        this.mapChanged = true;
    }

    private setupComponent() {
        this.isLoading = true;
        const mapRequest = this.getMapObservable();
        const functionalitiesRequest = this.getFunctionalitiesObservable();
        const coveragesRequest = this.getSiteEquipmentObservable();

        this.isEditable = false;

        observableZip(mapRequest, functionalitiesRequest, coveragesRequest)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                    this.$scope.$applyAsync();
                    this.mapChanged = false;
                }),
            )
            .subscribe(
                () => {
                    this.setupProjectionMap();
                },
                () => {
                    //
                },
            );
    }

    private setupProjectionMap() {
        forEach(this.functionalities.data, (fn) => {
            if (fn.videoStream) {
                fn.videoStream = this.videoStreamsLookup[fn.videoStream.id];
            }

            if (fn.viewCoverage) {
                if (fn.viewCoverage.functionalities) {
                    fn.viewCoverage.functionalities.splice(0, fn.viewCoverage.functionalities.length);
                }
                if (fn.viewCoverage.functionalities) {
                    fn.viewCoverage.siteEquipment.functionalities.splice(
                        0,
                        fn.viewCoverage.siteEquipment.functionalities.length,
                    );
                }

                fn.viewCoverage = this.viewCoveragesLookup[fn.viewCoverage.id];
                fn.viewCoverage.functionalities.push(fn);
                fn.viewCoverage.siteEquipment.functionalities.push(fn);
            }
        });
        forEach(this.siteEquipment.data, (se) => {
            se.isConnectedToEquipment = false;
            if (se.equipment !== null) {
                se.isConnectedToEquipment = true;
            }

            if (se.videoStreams === null) {
                se.videoStreams = [];
            }

            this.coverageCalculationsService.toAbsolute(se.positionOnMap, this.map.data.image);

            if (se.positionOnMap) {
                if (se.rotationOnMap === null || se.equipmentElevation === null) {
                    se.hasLegacyConfiguration = true;
                }

                se.rotationOnMap = se.rotationOnMap || 0;
                se.equipmentElevation = se.equipmentElevation || 5;
            }

            forEach(se.viewCoverages, (vc) => {
                if (vc.downwardTilt === null) {
                    se.hasLegacyConfiguration = true;
                }

                vc.downwardTilt = vc.downwardTilt || 15;
                vc.rotationOnMap = vc.rotationOnMap || 0;
                vc.horizontalFieldOfView = vc.horizontalFieldOfView || 60;
                vc.aspectRatio = vc.aspectRatio || 4 / 3;

                this.coverageCalculationsService.polygonToAbsolute(vc.mapCoveragePolygon, this.map.data.image);

                // cameraCoveragePolygon is in normalized coordinates also internally

                this.recalculateViewCoverage(vc);

                if (this.mapChanged) {
                    const viewCoverage = cloneDeep(vc.mapCoveragePolygon);
                    this.coverageCalculationsService.polygonToNormalized(viewCoverage, this.map.data.image);

                    const patch = {
                        mapCoveragePolygon: viewCoverage,
                    } as IViewCoverage;
                    this.siteEquipmentRepository.patchViewCoverage(this.site.id, se.id, vc.id, patch).subscribe();
                }
            });
        });
    }

    private getMapObservable(): Observable<IResponse<IMap>> {
        return this.sitesRepository.getMap(this.site.id).pipe(
            tap(
                (response) => {
                    this.map = response;
                    this.coverageCalculationsService.polygonToAbsolute(
                        this.map.data.scaleCoordinates,
                        this.map.data.image,
                    );
                },
                (error) => {
                    this.map = error;
                },
            ),
        );
    }

    private getFunctionalitiesObservable(): Observable<IResponse<IFunctionality[]>> {
        return this.siteFunctionalitiesRepository.getFunctionalitiesForSite(this.site.id).pipe(
            tap(
                (response) => {
                    this.functionalities = response;
                },
                (error) => {
                    this.functionalities = error;
                },
            ),
        );
    }

    private getSiteEquipmentObservable(): Observable<IResponse<ISiteEquipmentWithCoverages[]>> {
        return this.siteEquipmentListService.fetchList(this.site.id).pipe(
            tap(
                (response) => {
                    this.siteEquipment = response;
                    this.viewCoveragesLookup = {};
                    this.videoStreamsLookup = {};

                    forEach(this.siteEquipment.data, (se) => {
                        se.functionalities = [];

                        forEach(se.viewCoverages, (vc) => {
                            this.viewCoveragesLookup[vc.id] = vc;

                            vc.functionalities = [];
                            vc.siteEquipment = se;
                        });

                        forEach(se.videoStreams, (vs) => {
                            this.videoStreamsLookup[vs.id] = vs;
                        });
                    });
                },
                (error) => {
                    this.siteEquipment = error;
                },
            ),
        );
    }

    private selectedSiteEquipmentWatcherExpression() {
        if (this.selectedSiteEquipment === null) {
            return null;
        }

        return {
            equipmentElevation: this.selectedSiteEquipment.equipmentElevation,
            id: this.selectedSiteEquipment.id,
            positionOnMap: this.selectedSiteEquipment.positionOnMap,
            rotationOnMap: this.selectedSiteEquipment.rotationOnMap,
            viewCoverages: map(this.selectedSiteEquipment.viewCoverages, (viewCoverage) => {
                return {
                    aspectRatio: viewCoverage.aspectRatio,
                    downwardTilt: viewCoverage.downwardTilt,
                    horizontalFieldOfView: viewCoverage.horizontalFieldOfView,
                    id: viewCoverage.id,
                    minDownwardTilt: viewCoverage.minDownwardTilt,
                    rotationOnMap: viewCoverage.rotationOnMap,
                    streamSplitType: viewCoverage.streamSplitType,
                };
            }),
        };
    }

    private selectedSiteEquipmentWatcher(newValue: ISiteEquipmentWithCoverages, oldValue: ISiteEquipmentWithCoverages) {
        if (!oldValue || !newValue) {
            return;
        }

        if (newValue.id === this.selectedSiteEquipment.id && this.selectedSiteEquipment.hasLegacyConfiguration) {
            return;
        }

        const positionChanged = !isEqual(oldValue.positionOnMap, newValue.positionOnMap);
        const elevationChanged = !isEqual(oldValue.equipmentElevation, newValue.equipmentElevation);
        const rotationChanged = !isEqual(oldValue.rotationOnMap, newValue.rotationOnMap);

        if (elevationChanged || rotationChanged) {
            const patchObject = {} as ISiteEquipmentWithCoverages;
            if (elevationChanged) {
                patchObject.equipmentElevation = newValue.equipmentElevation;
            }
            if (rotationChanged) {
                patchObject.rotationOnMap = newValue.rotationOnMap;
            }

            if (rotationChanged && newValue.viewCoverages.length === 1) {
                newValue.viewCoverages[0].rotationOnMap = newValue.rotationOnMap;
                this.selectedSiteEquipment.viewCoverages[0].rotationOnMap = newValue.rotationOnMap;
            }

            if (!this.debouncers[newValue.id]) {
                this.debouncers[newValue.id] = this.debounce(500, (patch: any) => {
                    this.selectedSiteEquipment.isSaving = true;

                    this.siteEquipmentRepository.patch(this.site.id, newValue.id, patch).subscribe(
                        (response: IResponseWithLoading<ISiteEquipmentWithCoverages>) => {
                            if (!response.hasError && response.data) {
                                this.patchSiteEquipmentResponse = response;
                                this.selectedSiteEquipment.isSaving = false;
                            }
                        },
                        (error) => {
                            this.patchSiteEquipmentResponse = error;
                            this.selectedSiteEquipment.isSaving = false;
                        },
                    );
                });
            }
            this.debouncers[newValue.id](patchObject);
        }

        forEach(newValue.viewCoverages, (newVc, index) => {
            const oldVc = oldValue.viewCoverages[index];

            if (newVc.horizontalFieldOfView && newVc.aspectRatio) {
                /* eslint-disable @typescript-eslint/no-unused-vars */
                // Update lower limit for the downtilt
                const [hFovRad, vFovRad] = this.coverageCalculationsService.getFieldOfViewsInRadians(newVc);
                /* eslint-enable @typescript-eslint/no-unused-vars */

                const newMinLimit = this.coverageCalculationsService.getMinDownwardTilt(newVc);
                if (newMinLimit !== this.selectedSiteEquipment.viewCoverages[index].minDownwardTilt) {
                    this.selectedSiteEquipment.viewCoverages[index].minDownwardTilt = newMinLimit;

                    if (newVc.downwardTilt < newMinLimit) {
                        this.selectedSiteEquipment.viewCoverages[index].downwardTilt =
                            this.selectedSiteEquipment.viewCoverages[index].minDownwardTilt;
                    }
                }
            }

            if (oldVc) {
                const vcRotationChanged = !isEqual(newVc.rotationOnMap, oldVc.rotationOnMap);
                const vcFovChanged = !isEqual(newVc.horizontalFieldOfView, oldVc.horizontalFieldOfView);
                const vcTiltChanged = !isEqual(newVc.downwardTilt, oldVc.downwardTilt);
                const vcAspectRatioChanged = !isEqual(newVc.aspectRatio, oldVc.aspectRatio);
                const vcStreamSplitTypeChanged = !isEqual(newVc.streamSplitType, oldVc.streamSplitType);

                const configurationChanged =
                    positionChanged ||
                    elevationChanged ||
                    vcRotationChanged ||
                    vcFovChanged ||
                    vcTiltChanged ||
                    vcAspectRatioChanged ||
                    vcStreamSplitTypeChanged;

                if (configurationChanged) {
                    const viewCoverage = this.selectedSiteEquipment.viewCoverages[index];

                    this.recalculateViewCoverage(viewCoverage);

                    if (!this.patchDeltas[viewCoverage.id]) {
                        this.patchDeltas[viewCoverage.id] = {} as IViewCoverage;
                    }
                    const vcPatchObject = {
                        ...this.patchDeltas[viewCoverage.id],
                    } as IViewCoverage;

                    vcPatchObject.mapCoveragePolygon = cloneDeep(viewCoverage.mapCoveragePolygon);
                    this.coverageCalculationsService.polygonToNormalized(
                        vcPatchObject.mapCoveragePolygon,
                        this.map.data.image,
                    );
                    vcPatchObject.cameraCoveragePolygon = cloneDeep(viewCoverage.cameraCoveragePolygon);
                    // cameraCoveragePolygon is already in normalized coordinates

                    if (vcRotationChanged) {
                        vcPatchObject.rotationOnMap = viewCoverage.rotationOnMap;
                    }
                    if (vcFovChanged) {
                        vcPatchObject.horizontalFieldOfView = viewCoverage.horizontalFieldOfView;
                    }
                    if (vcTiltChanged) {
                        vcPatchObject.downwardTilt = viewCoverage.downwardTilt;
                    }
                    if (vcAspectRatioChanged) {
                        vcPatchObject.aspectRatio = viewCoverage.aspectRatio;
                    }
                    if (vcStreamSplitTypeChanged) {
                        vcPatchObject.streamSplitType = StreamSplitType[viewCoverage.streamSplitType] as any;
                    }

                    if (vcRotationChanged && !rotationChanged && newValue.viewCoverages.length === 1) {
                        this.selectedSiteEquipment.rotationOnMap = viewCoverage.rotationOnMap;
                    }
                    if (!this.debouncers[viewCoverage.id]) {
                        this.debouncers[viewCoverage.id] = this.debounce(500, (patch: IViewCoverage) => {
                            delete this.patchDeltas[viewCoverage.id];
                            viewCoverage.isSaving = true;

                            this.siteEquipmentRepository
                                .patchViewCoverage(this.site.id, newValue.id, viewCoverage.id, patch)
                                .subscribe(
                                    (response: IResponseWithLoading<IViewCoverage>) => {
                                        if (!response.hasError) {
                                            viewCoverage.isSaving = false;

                                            if (vcStreamSplitTypeChanged) {
                                                viewCoverage.streamSplitName = response.data.streamSplitName;
                                                viewCoverage.streamSplitIcon = response.data.streamSplitIcon;
                                            }

                                            this.patchViewCoverageResponse = response;
                                        }
                                    },
                                    (error) => {
                                        viewCoverage.isSaving = false;
                                        this.patchViewCoverageResponse = error;
                                    },
                                );
                        });
                    }
                    this.debouncers[viewCoverage.id](vcPatchObject);
                    this.patchDeltas[viewCoverage.id] = { ...vcPatchObject };
                }
            }
        });
    }

    private recalculateViewCoverage(viewCoverage: IViewCoverage) {
        this.coverageCalculationsService.calculateMapCoverage(this.map.data, viewCoverage);
    }

    private updateScreenSizeRestrictions() {
        if (this.screenService.isTablet(false)) {
            this.isCollapsed = true;
        }

        if (this.screenService.isMobile() && this.isEditable) {
            this.toggleEditMode();
        }

        if (this.screenService.isInLandscapeMode() && this.screenService.isTablet(true) && !this.isEditable) {
            this.toggleEditMode();
        }
    }

    private addViewCoverage(siteEquipment: ISiteEquipmentWithCoverages) {
        const cameraCoveragePolygon: IPoint[] = [
            {
                x: 0,
                y: 0,
            },
            {
                x: 1,
                y: 0,
            },
            {
                x: 1,
                y: 1,
            },
            {
                x: 0,
                y: 1,
            },
        ];

        const newViewCoverage = {
            aspectRatio: 4 / 3,
            cameraCoveragePolygon,
            downwardTilt: 15,
            horizontalFieldOfView: 60,
            rotationOnMap: siteEquipment.rotationOnMap,
            siteEquipment,
        } as IViewCoverage;

        this.coverageCalculationsService.calculateMapCoverage(this.map.data, newViewCoverage);
        newViewCoverage.siteEquipment = null;

        this.coverageCalculationsService.polygonToNormalized(newViewCoverage.mapCoveragePolygon, this.map.data.image);

        extendWithLoadingAndFail(
            this.siteEquipmentRepository.addViewCoverage(this.site.id, siteEquipment.id, newViewCoverage),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((response: IResponseWithLoading<IViewCoverage>) => {
                if (!response.hasError && response.data) {
                    response.data.siteEquipment = siteEquipment;
                    this.coverageCalculationsService.polygonToAbsolute(
                        response.data.mapCoveragePolygon,
                        this.map.data.image,
                    );
                    response.data.functionalities = [];
                    siteEquipment.viewCoverages.push(response.data);
                }
            });
    }
}
